import { Finding } from "@pages/findings/types/finding";
import { Policy } from "@pages/policy/types";
import { Repo } from "@pages/projects/types/repo";

import { AuthProvider } from "./authProvider";
import { SsoProviderType } from "./ssoProviderType";

export type TriageState = "untriaged" | "ignored" | "reopened";
export type AggregateState =
  | "open"
  | "ignored-app"
  | "ignored-code"
  | "fixed"
  | "removed";

export interface PolicyFilterItem {
  name: string;
  slug: string;
}

export type RefType = "Branch" | "PR";
export type IssueItem = {
  type: RefType;
  ref: string;
};

type RulesetType = "Ruleset" | "Rule";
export interface RulesetFilterItem {
  name: string;
  type: RulesetType;
}

export interface FindingFilterItems {
  repoNames: string[];
  policyItems: PolicyFilterItem[];
  rulesetItems: RulesetFilterItem[];
  refs: string[];
  repoRefs?: {
    [key: string]: string[];
  };
}

export interface UpdateFindingBody {
  triageState: TriageState;
  note?: string;
}

export interface FetchFindingsResponse {
  fetchedFindings: Finding[];
  // TODO: make fetchedCounts non-optional after removing
  // or cleaning up overview and analytics pages
  fetchedCounts?: FindingCounts;
  fetchedNextUrl?: string;
  fetchedFilterItems: FindingFilterItems;
  cursor?: string;
}

export interface FetchDemoFindingsResponse {
  issues: Finding[];
  counts: FindingCounts;
  filters: FindingFilterItems;
}

export interface RepoChanges {
  tracked_branches?: string[] | null;
  policies?: Policy[];
  ignored_files?: string[] | null;
  inventory?: boolean;
  autofix?: boolean;
  tags?: string[];
  sca_parity_rules?: boolean;
  sca_provisioned?: boolean;
}

export interface Scan {
  id?: number;
  deployment_id?: number;
  repository_id: number;
  completed_at?: string;
  started_at?: string;
  exit_code?: number;
  meta?: {
    ci_job_url?: string;
    commit?: string;
    commit_author_username?: string;
    commit_author_name?: string;
    commit_author_email?: string;
    repo_url?: string;
    pull_request_id?: number;
    branch?: string;
  };
  stats?: {
    findings?: number;
  };
}

export interface DeploymentScansResponse {
  scans: Scan[];
}

export interface DeploymentRepoResponse {
  repo: Repo;
}

export interface DeploymentRepoListResponse {
  repos: Repo[];
}

export interface DeploymentReposRefreshResponse {
  new_repos: string[];
}
export interface SubmitDeploymentRepoResponse extends Repo {}

export interface GitlabGroup {
  id: number;
  name: string;
  is_enabled: boolean;
}

export interface DeploymentRepoConfigResponse {
  config: string;
}

export interface FetchGitlabGroupsSelectionsResponse {
  gitlab_organizations: GitlabGroup[];
}

export interface UpdateGitlabGroupsSelectionsRequest {
  organizations: GitlabGroup[];
}

export interface FindingCounts {
  open: number;
  ignored: number;
  closed: number;
}

export interface UserPermissionsResponse {
  authorized_actions: string[];
}

export interface DeploymentTagsResponse {
  deployment_id: number;
  repository_tags: string[];
}

/**
 * User Invite Types
 */
export enum UserInviteStatus {
  PendingInvite = "pending_invite",
  Invited = "invited",
  Registering = "registering",
  LoggedIn = "logged_in",
  Joined = "joined",
  Rejected = "rejected",
  Timeout = "timeout",
}

export interface Organization {
  id: number;
  name?: string;
  slug: string;
  deployments?: { id: number; name: string }[];
}

export interface UserInvite {
  id: number;
  organization_id: number;
  invited_email: string;
  expiration_time: string;
  status: UserInviteStatus;
  created_by_user_id?: string;
  organization?: Organization;
}

export interface UserInviteResponse {
  user_invite: UserInvite;
}

export interface ScanResponse {
  scan: Scan;
}

export interface SamlProviderData {
  x509cert: string;
  idpSsoUrl: string;
  idpIssuer: string;
}

export interface OpenIdProviderData {
  clientId: string;
  clientSecret: string;
}
export interface SsoProviderSettings extends AuthProvider {
  deployment_id: string | null;
  provider_type: SsoProviderType;
  provider_data: OpenIdProviderData | SamlProviderData;
}

export interface GetSsoTokenEmailResponse {
  email_domain: string;
}

export interface PostSsoDeploymentSettings extends SsoProviderSettings {
  deployment_name: string;
  token: string;
}
